import { DialogActions, DialogContent, Typography } from '@mui/material'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import NewSwitch from 'ui/atoms/NewSwitch/NewSwitch'
import ConfirmDialog from 'ui/components/ConfirmDialog/ConfirmDialog'
import { Dialog } from 'ui/components/Dialog/Dialog'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import CustomDialogTitle from 'ui/components/SettingsModal/components/CustomDialogTitle/CustomDialogTitle'
import ModalButtons from 'ui/molecules/ModalButtons/ModalButtons'
import Datetime from 'utils/datetime/datetime'
import { type ElplanEditorSession, type ElplanModeSwitchOutcome, isElplanSlotUnit } from './ElplanEditor.types'
import { ElplanEditorTable } from './ElplanEditorTable'
import { getElplanSaveBlockCounts } from './faults'
import { useElplanEditor } from './useElplanEditor'
import { useElplanEditorSave } from './useElplanEditorSave'

type ElplanEditorModalProps = {
  session: ElplanEditorSession
  refetchPlan: () => Promise<unknown>
  onClose: () => void
}

type ElplanEditorDialog = { kind: 'discard' } | { kind: 'flatten'; changedHourCount: number } | null

export function ElplanEditorModal({ session, refetchPlan, onClose }: ElplanEditorModalProps): ReactNode {
  const { t } = useTranslation()
  const {
    draft,
    mode,
    expandedHourStarts,
    faults,
    isDirty,
    changedSlotStarts,
    applyEdits,
    applyTypedEdits,
    releaseTypedEdits,
    switchMode,
    toggleHour,
    setAllHoursExpanded,
  } = useElplanEditor(session)
  const { save, isPending } = useElplanEditorSave({ target: session.target, refetchPlan, onSaved: onClose })
  const [dialog, setDialog] = useState<ElplanEditorDialog>(null)

  const title = session.target.kind === 'create' ? t('Create electricity plan') : t('Edit electricity plan')
  const day = Datetime.toLocalTime(draft.slotStarts[0], 'onlyDate')
  const weekday = Datetime.toLocalTime(draft.slotStarts[0], 'shortDayText')
  function getSaveBlockMessage(): string {
    const { blank, invalid } = getElplanSaveBlockCounts(draft, faults, mode)
    if (blank === 0 && invalid === 0) {
      return ''
    }
    if (invalid === 0) {
      return t('Fill in {{count}} empty values to save', { count: blank })
    }
    if (blank === 0) {
      return t('Correct {{count}} invalid values to save', { count: invalid })
    }
    return t('Values to fix before saving: {{blank}} empty, {{invalid}} invalid', { blank, invalid })
  }

  function getSwitchRefusalMessage(partlyBlankHourCount: number, faultedHourCount: number): string {
    if (faultedHourCount === 0) {
      return t('Fill in or clear the empty quarters in {{count}} hours to switch to hour mode', {
        count: partlyBlankHourCount,
      })
    }
    if (partlyBlankHourCount === 0) {
      return t('Correct the invalid quarters in {{count}} hours to switch to hour mode', { count: faultedHourCount })
    }
    return t(
      'Hours to fix before switching to hour mode: {{blank}} with empty quarters, {{invalid}} with invalid quarters',
      {
        blank: partlyBlankHourCount,
        invalid: faultedHourCount,
      }
    )
  }

  function handleSwitchOutcome(outcome: ElplanModeSwitchOutcome): void {
    if (outcome.kind === 'refused') {
      alertStore.warning(getSwitchRefusalMessage(outcome.partlyBlankHourCount, outcome.faultedHourCount))
    }
    if (outcome.kind === 'needsConfirmation') {
      setDialog({ kind: 'flatten', changedHourCount: outcome.changedHourCount })
    }
  }

  function handleModeClick(value: string): void {
    if (isElplanSlotUnit(value)) {
      handleSwitchOutcome(switchMode(value))
    }
  }

  function handleFlattenConfirm(): void {
    setDialog(null)
    handleSwitchOutcome(switchMode('hour', { acceptValueChanges: true }))
  }

  const saveBlockMessage = getSaveBlockMessage()

  function handleClose(): void {
    if (isPending) {
      return
    }
    if (isDirty) {
      setDialog({ kind: 'discard' })
      return
    }
    onClose()
  }

  return (
    <>
      <Dialog
        open
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        slotProps={{ paper: { 'aria-label': `${title} ${day}`, sx: { height: '100%' } } }}
      >
        <CustomDialogTitle title={title} handleClose={handleClose} />
        <DialogContent className="flex flex-col">
          <div className="pb-3">
            <Typography>
              <strong>{`${t('Date')}:`}</strong> {`${weekday} ${day}`}
            </Typography>
          </div>
          {draft.resolution === 'quarter' && (
            <div className="mb-2 flex items-center justify-between gap-2">
              <NewSwitch
                size="compact"
                items={[
                  { value: 'hour', label: t('Hour') },
                  { value: 'quarter', label: t('Quarter') },
                ]}
                value={mode}
                disabled={isPending}
                onClick={handleModeClick}
              />
            </div>
          )}
          <div className="flex min-h-0 flex-1 flex-col" inert={isPending}>
            <ElplanEditorTable
              draft={draft}
              mode={mode}
              expandedHourStarts={expandedHourStarts}
              faults={faults}
              changedSlotStarts={changedSlotStarts}
              onEdits={applyEdits}
              onTypedEdits={applyTypedEdits}
              onTypingEnd={releaseTypedEdits}
              onToggleHour={toggleHour}
              onSetAllHoursExpanded={setAllHoursExpanded}
            />
          </div>
          {session.target.kind === 'update' && (
            <InfoBanner
              style="warning"
              text={t(
                'You are currently editing an already created electricity plan. Any changes you make to this plan may differ from the plan you have sent into trading.'
              )}
            />
          )}
        </DialogContent>
        <DialogActions>
          <div className="flex w-full flex-col items-end gap-2">
            {saveBlockMessage && <InfoBanner style="warning" text={saveBlockMessage} />}
            <ModalButtons
              submitLabel={t('Save')}
              isSubmitting={isPending}
              disableSubmit={faults.blocksSave}
              onSubmit={() => void save(draft)}
              onCancel={handleClose}
            />
          </div>
        </DialogActions>
      </Dialog>
      {dialog?.kind === 'flatten' && (
        <ConfirmDialog
          open
          initialFocus="deny"
          onClose={() => setDialog(null)}
          onConfirm={handleFlattenConfirm}
          textObject={{
            title: t('Switch to hour mode?'),
            text: t('{{count}} hours will change: each gets the mean of its quarters, rounded to one decimal.', {
              count: dialog.changedHourCount,
            }),
            confirm: t('Switch to hour mode'),
            deny: t('Keep quarters'),
          }}
        />
      )}
      <ConfirmDialog
        open={dialog?.kind === 'discard'}
        initialFocus="deny"
        onClose={() => setDialog(null)}
        onConfirm={onClose}
        textObject={{
          title: t('Do you really want to close without saving?'),
          text: t('Currently you have unsaved changes, are you completely sure you would like to exit without saving?'),
          confirm: t('Discard changes'),
          deny: t('Go back to editing'),
        }}
      />
    </>
  )
}
